/**
 * The dinosaur shop. Builds one row per tier once, then on each frame updates
 * only the attributes and text that changed, so it can be refreshed from the
 * render loop without rebuilding DOM or thrashing layout.
 */

import { DINOS, aOrAn } from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import {
  canAffordDino,
  dinoCost,
  dinoEpsAt,
  dinoLevel,
  epsBreakdown,
  isDinoRevealed,
} from '../game/economy';
import { formatRate, formatShort } from '../game/numbers';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';
import type { TooltipLine } from './tooltip';
import { dinoSpriteUrl, opaqueRows } from './sprites';
import { eligibility, isEvolved, speciesFlavor, speciesName, spriteIdFor } from '../game/inventory';
import { growthStage } from './yardGrowth';
import type { ItemDef } from '../data/items';

export interface ShopView {
  /** `boost` is the active falling-egg multiplier, so rows match the EPS readout. */
  update(state: GameState, boost: number): void;
  /**
   * While an item is armed the rows become targets: an eligible row says
   * what the dinosaur would become and clicking it uses the item; the rest
   * are disabled with the reason. Null returns the rows to buying.
   */
  setTargetMode(item: ItemDef | null): void;
}

interface Row {
  def: DinoDef;
  button: HTMLButtonElement;
  cost: HTMLElement;
  /** What the price buys: "hatch" before a tier has a level, else nothing. */
  per: HTMLElement;
  /** The egg and the figure together, hidden while an item is armed. */
  price: HTMLElement;
  level: HTMLElement;
  /** The figure alone; the "Lvl" beside it never changes. */
  levelValue: HTMLElement;
  name: HTMLElement;
  /**
   * The line under the name: the stage of life its level has reached, then the
   * species when named — or, while a splice is armed, what it would do here.
   */
  species: HTMLElement;
  // Last-rendered values, so unchanged frames write nothing to the DOM.
  lastRevealed: boolean | null;
  lastAffordable: boolean | null;
  lastCost: string | null;
  /** The sprite's img, so an evolution can swap it. */
  icon: HTMLImageElement;
  lastLevel: string | null;
  lastName: string | null;
  lastSpecies: string | null;
  lastSprite: string | null;
  lastEvolved: boolean | null;
  lastArming: boolean | null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function buildRow(def: DinoDef, onBuy: (def: DinoDef) => void): Row {
  const button = el('button', 'dino');
  button.type = 'button';
  button.hidden = true;

  // The tier's sprite, falling back to the coloured initial until its PNG lands.
  const icon = el('span', 'dino__icon', def.name.charAt(0));
  icon.style.background = def.color;
  const img = new Image();
  img.alt = '';
  img.addEventListener('load', () => {
    // Sprites keep their feet on the bottom row for the yard, which leaves
    // empty rows above; crop to what's drawn so the icon centres on the dino.
    const { top, bottom } = opaqueRows(img);
    icon.style.width = `${img.naturalWidth}px`;
    icon.style.height = `${bottom - top + 1}px`;
    img.style.width = `${img.naturalWidth}px`;
    img.style.height = `${img.naturalHeight}px`;
    img.style.marginTop = `-${top}px`;
    icon.textContent = '';
    icon.style.background = '';
    icon.classList.add('dino__icon--sprite');
    icon.append(img);
  });
  img.src = dinoSpriteUrl(def.id, 'full');

  const body = el('span', 'dino__body');
  const name = el('span', 'dino__name', def.name);
  const species = el('span', 'dino__species');
  species.hidden = true;
  body.append(name, species);

  // The numbers stack on the right: the level (or "hatch", before there is
  // one) on top, then what the next one costs, the same way round on every row.
  const nums = el('span', 'dino__nums');
  // Two elements, because the word and the figure are not the same thing: the
  // word only identifies the number, so it is smaller and grey, the way the
  // species line sits under the name.
  const level = el('span', 'dino__level');
  const levelValue = el('span', 'dino__level-value');
  // The space is for anything reading the text rather than the pixels: a flex
  // container does not render a whitespace-only item, so the gap below is
  // still what separates them on screen.
  level.append(el('span', 'dino__level-label', 'Lvl'), ' ', levelValue);
  const price = el('span', 'dino__price');
  const eggIcon = document.createElement('img');
  eggIcon.className = 'dino__egg';
  eggIcon.alt = '';
  eggIcon.src = `${import.meta.env.BASE_URL}art/icons/egg.png`;
  const cost = el('span', 'dino__cost');
  price.append(eggIcon, cost);
  const per = el('span', 'dino__per');
  nums.append(level, per, price);

  button.append(icon, body, nums);
  button.addEventListener('click', () => onBuy(def));
  bindTooltip(button, () => [{ text: flavorOf(def) }, ...rateLines(def)]);

  return {
    def,
    button,
    cost,
    per,
    price,
    level,
    levelValue,
    name,
    species,
    icon: img,
    lastRevealed: null,
    lastAffordable: null,
    lastCost: null,
    lastLevel: null,
    lastName: null,
    lastSpecies: null,
    lastSprite: null,
    lastEvolved: null,
    lastArming: null,
  };
}

/** What a row says while an item is armed: the evolution, or why not. */
function targetOf(item: ItemDef, def: DinoDef, state: GameState): string {
  const e = eligibility(item, def, state);
  return e.ok ? `becomes ${aOrAn(def.evolved.name)} ${def.evolved.name}` : e.reason;
}

/** The state the tooltips read; set on every update so they stay current. */
let tooltipState: GameState | null = null;
/** The boost in force, so the tooltip's rates match the stats panel's. */
let tooltipBoost = 1;

/**
 * What this tier lays and what one more level would add, with every
 * multiplier applied. It used to sit under the cost on every row, which made
 * the list busy; it is worth a hover rather than a line each.
 */
function rateLines(def: DinoDef): TooltipLine[] {
  const state = tooltipState;
  if (!state) return [];
  const level = dinoLevel(def, state);
  // Before hatching, what level 1 would lay with everything in force now.
  if (level === 0) {
    const first = dinoEpsAt(def, 1, state, tooltipBoost);
    return [{ text: `${formatRate(first)} eggs/s at level 1`, muted: true }];
  }
  const total = epsBreakdown(state, tooltipBoost)[def.id] ?? 0;
  return [
    { text: `Laying ${formatRate(total)}/s`, muted: true },
    { text: `Next level +${formatRate(total / level)}/s`, muted: true },
  ];
}
const flavorOf = (def: DinoDef): string =>
  tooltipState ? speciesFlavor(def, tooltipState) : def.flavor;

export function createShopView(
  root: HTMLElement,
  onBuy: (def: DinoDef) => void,
  onUseItem: (item: ItemDef, def: DinoDef) => void = () => {},
): ShopView {
  /** The armed item, while the player is choosing a target. */
  let targetItem: ItemDef | null = null;
  const empty = el('p', 'shop__empty', 'Click the egg to hatch your first dinosaur.');
  const list = el('div', 'shop__list');
  root.replaceChildren(empty, list);

  const rows = DINOS.map((def) =>
    buildRow(def, (d) => {
      if (targetItem) onUseItem(targetItem, d);
      else onBuy(d);
    }),
  );
  for (const row of rows) list.append(row.button);

  let lastAnyRevealed: boolean | null = null;

  return {
    setTargetMode(item) {
      targetItem = item;
      root.classList.toggle('shop--targeting', item !== null);
      // Everything a row shows depends on the mode, so redraw it all.
      for (const row of rows) {
        row.lastAffordable = null;
        row.lastCost = null;
        row.lastLevel = null;
      }
    },
    update(state, boost) {
      tooltipState = state;
      tooltipBoost = boost;
      let anyRevealed = false;

      for (const row of rows) {
        const revealed = isDinoRevealed(row.def, state);
        anyRevealed ||= revealed;
        // While a splice is held, a dinosaur that has already evolved is not
        // a choice at all, so it steps out of the list rather than sitting
        // greyed out between the ones that are.
        const shown = revealed && !(targetItem !== null && isEvolved(row.def, state));
        if (shown !== row.lastRevealed) {
          row.lastRevealed = shown;
          row.button.hidden = !shown;
        }
        if (!shown) continue;

        // While an item is armed the row is a target, not a buy button.
        const target = targetItem ? eligibility(targetItem, row.def, state) : null;
        const usable = target === null ? canAffordDino(row.def, state) : target.ok;
        if (usable !== row.lastAffordable) {
          row.lastAffordable = usable;
          row.button.disabled = !usable;
          row.button.classList.toggle('dino--target', target !== null && target.ok);
        }

        // While an item is armed the price gives way to the target line.
        const arming = targetItem !== null;
        if (arming !== row.lastArming) {
          row.lastArming = arming;
          row.price.hidden = arming;
        }

        // A space before the scale ("62 M") here; the yard's floats go without.
        const costText = formatShort(dinoCost(row.def, state), ' ');
        if (costText !== row.lastCost) {
          row.lastCost = costText;
          row.cost.textContent = costText;
        }

        // The name line is the pet name if there is one, so it never loses
        // its room to the species; an evolved tier shows its evolved species.
        const species = speciesName(row.def, state);
        const petName = state.names[row.def.id] ?? '';
        const label = petName ? `"${petName}"` : species;
        if (label !== row.lastName) {
          row.lastName = label;
          row.name.textContent = label;
        }

        // Under it: the species once named and the stage it has grown to
        // ("Grown Torosaurus"), or what an armed splice would do here.
        const stage = growthStage(state.dinos[row.def.id] ?? 0);
        const second = targetItem
          ? targetOf(targetItem, row.def, state)
          : [stage, petName ? species : ''].filter((part) => part !== '').join(' ');
        if (second !== row.lastSpecies) {
          row.lastSpecies = second;
          row.species.textContent = second;
          row.species.hidden = second === '';
        }

        const evolved = isEvolved(row.def, state);
        if (evolved !== row.lastEvolved) {
          row.lastEvolved = evolved;
          row.level.classList.toggle('dino__level--evolved', evolved);
        }
        const spriteId = spriteIdFor(row.def, state);
        if (spriteId !== row.lastSprite) {
          row.lastSprite = spriteId;
          row.icon.src = dinoSpriteUrl(spriteId, 'full');
        }

        // An unhatched tier has no level to show, and its price buys a
        // hatching rather than a level.
        const level = dinoLevel(row.def, state);
        const levelText = level > 0 ? String(level) : '';
        if (levelText !== row.lastLevel) {
          row.lastLevel = levelText;
          row.levelValue.textContent = levelText;
          row.level.hidden = level === 0;
          // The level line already says it, so a "+1 lvl" under the price
          // only repeated it. An unhatched tier shows no level, and its price
          // buys a hatching rather than a level, so that one still says so.
          row.per.textContent = level > 0 ? '' : 'Hatch';
        }
        row.per.hidden = arming || level > 0;
      }

      if (anyRevealed !== lastAnyRevealed) {
        lastAnyRevealed = anyRevealed;
        empty.hidden = anyRevealed;
      }
    },
  };
}
